import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Subscription } from "@/integrations/supabase/types/";
import { SubscriptionTableRow } from "./SubscriptionTableRow";
import { EditSubscriptionDialog } from "./EditSubscriptionDialog";
import { PaymentDetailsDialog } from "./PaymentDetailsDialog";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNotifications } from "@/hooks/use-notifications";

interface SubscriptionTableProps {
  subscriptions: Subscription[];
}

export const SubscriptionTable = ({ subscriptions }: SubscriptionTableProps) => {
  const { toast } = useToast();
  const { sendNotification } = useNotifications();
  const queryClient = useQueryClient();
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [viewingSubscription, setViewingSubscription] = useState<Subscription | null>(null);
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const status = searchParams.get('status');
    const code = searchParams.get('code');

    if (status === 'success') {
      toast({
        title: "Payment Successful",
        description: "Your subscription has been activated",
      });
    } else if (status === 'error') {
      toast({
        variant: "destructive",
        title: "Payment Failed",
        description: "There was an error processing your payment",
      });
    }

    if (code) {
      handleOAuthCallback(code);
    }
  }, [location]);

  const handleOAuthCallback = async (code: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('youcanpay-oauth', {
        body: { code },
      });

      if (error) throw error;

      if (data.access_token) {
        toast({
          title: "Success",
          description: "YouCanPay account connected successfully",
        });
      }
    } catch (error) {
      console.error("Error handling OAuth callback:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to connect YouCanPay account",
      });
    }
  };

  const handleSendNotification = async (subscription: Subscription, type: 'email' | 'sms' | 'both') => {
    try {
      if (!subscription.client_email && type !== 'sms') {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Client email is required for email notifications",
        });
        return;
      }

      if (!subscription.client_phone && type !== 'email') {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Client phone is required for SMS notifications",
        });
        return;
      }

      await sendNotification({
        to: subscription.client_email ? [subscription.client_email] : [],
        subject: `Subscription Update: ${subscription.plan_name}`,
        html: `
          <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Subscription Details</h2>
            <p>Plan: ${subscription.plan_name}</p>
            <p>Amount: ${subscription.currency || 'USD'} ${subscription.price}</p>
            <p>Status: ${subscription.payment_status}</p>
          </div>
        `,
        type,
        phoneNumbers: subscription.client_phone ? [subscription.client_phone] : [],
        message: `Your subscription to ${subscription.plan_name} (${subscription.currency || 'USD'} ${subscription.price}) is ${subscription.payment_status}`,
      });

      toast({
        title: "Success",
        description: `${type === 'both' ? 'Notifications' : type.toUpperCase()} sent successfully`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to send ${type} notification`,
      });
    }
  };

  const handleGenerateLink = async (subscription: Subscription) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-payment-link', {
        body: {
          amount: subscription.price,
          plan_name: subscription.plan_name,
          subscription_id: subscription.id,
        },
      });

      if (error) throw error;

      if (data.payment_url) {
        window.open(data.payment_url, '_blank');
        toast({
          title: "Success",
          description: "Payment link generated successfully",
        });
      }
    } catch (error) {
      console.error("Error generating payment link:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate payment link",
      });
    }
  };

  const handleSaveEdit = async (planName: string, price: string) => {
    if (!editingSubscription) return;

    try {
      const { error } = await supabase
        .from("subscriptions")
        .update({
          plan_name: planName,
          price: Number(price),
        })
        .eq("id", editingSubscription.id);

      if (error) throw error;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["subscriptions"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["analytics"] }),
      ]);
      
      toast({
        title: "Success",
        description: "Subscription updated successfully",
      });
      
      setEditingSubscription(null);
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update subscription. Please try again.",
      });
    }
  };

  const handleDelete = async (subscription: Subscription) => {
    if (!confirm(`Are you sure you want to delete this payment? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from("subscriptions")
        .delete()
        .eq("id", subscription.id);

      if (error) throw error;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["subscriptions"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["analytics"] }),
      ]);
      
      toast({
        title: "Success",
        description: "Payment deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting payment:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete payment. Please try again.",
      });
    }
  };

  return (
    <>
      <div className="rounded-md border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Plan Name</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subscriptions.map((subscription) => (
              <TableRow key={subscription.id} className="group">
                <SubscriptionTableRow
                  subscription={subscription}
                  onEdit={setEditingSubscription}
                  onView={setViewingSubscription}
                  onDelete={handleDelete}
                  onGenerateLink={handleGenerateLink}
                  onSendNotification={handleSendNotification}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <EditSubscriptionDialog
        subscription={editingSubscription}
        onClose={() => setEditingSubscription(null)}
        onSave={handleSaveEdit}
      />
      
      <PaymentDetailsDialog
        subscription={viewingSubscription}
        onClose={() => setViewingSubscription(null)}
      />
    </>
  );
};